import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { MapPin, Fish, Heart, Map, List, Search, Compass, SlidersHorizontal, Smartphone, Loader2, Sparkles, X } from "lucide-react";
import MapboxMap from "@/components/MapboxMap";
import { SearchResultsDropdown } from "@/components/destinations/SearchResultsDropdown";
import { MobileFilterSheet } from "@/components/destinations/MobileFilterSheet";
import { DirectionsDialog } from "@/components/destinations/DirectionsDialog";

export function MobileDestinationsLayout({
  mapProps, flyToLocation, filterProps,
  mobileSearchQuery, setMobileSearchQuery,
  mobileSearchResults, setMobileSearchResults,
  mobileSearchLoading, mobileSearchOpen, setMobileSearchOpen,
  mobileViewMode, setMobileViewMode,
  viewportDestinations, selectedState, setSelectedState, states,
  toggleFavorite,
  onDestinationNavigate, onSearchDestination, onSearchAccessPoint, onSearchLocation,
  directionsDialogOpen, setDirectionsDialogOpen,
  directionsDestination, directionsOrigin, setDirectionsOrigin, getDirections,
  navigate
}) {
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" data-testid="destinations-page-mobile">
      <div className="flex-1 relative">
        <MapboxMap
          {...mapProps}
          flyToLocation={flyToLocation}
          className="w-full h-full [&_.basemap-selector-container]:top-[72px] [&_.basemap-selector-container]:left-3 [&_.basemap-selector-container]:right-auto"
        />
        <div className="absolute top-3 left-3 right-3 z-[1000]">
          <div className="relative">
            <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-200 overflow-hidden">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search rivers, lakes..."
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  onFocus={() => mobileSearchResults && setMobileSearchOpen(true)}
                  className="w-full h-12 pl-12 pr-10 text-sm bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="mobile-search-input"
                />
                {mobileSearchLoading ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                ) : mobileSearchQuery && (
                  <button
                    onClick={() => { setMobileSearchQuery(""); setMobileSearchResults(null); setMobileSearchOpen(false); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <Sheet>
                <SheetTrigger asChild>
                  <button className="px-4 py-3 border-l border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="w-5 h-5 text-gray-600" />
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[70vh] rounded-t-xl">
                  <SheetHeader>
                    <SheetTitle className="font-serif">Filters</SheetTitle>
                  </SheetHeader>
                  <MobileFilterSheet {...filterProps} />
                </SheetContent>
              </Sheet>
            </div>
            
            {mobileSearchOpen && mobileSearchResults && (
              <div className="absolute top-full left-0 right-0 mt-2">
                <SearchResultsDropdown
                  searchResults={mobileSearchResults}
                  destinations={mapProps.destinations}
                  onSelectDestination={(dest) => { onSearchDestination(dest); setMobileSearchQuery(""); setMobileSearchResults(null); setMobileSearchOpen(false); }}
                  onSelectAccessPoint={(point) => { onSearchAccessPoint(point); setMobileSearchQuery(""); setMobileSearchResults(null); setMobileSearchOpen(false); }}
                  onSelectLocation={(loc) => { onSearchLocation(loc); setMobileSearchQuery(""); setMobileSearchResults(null); setMobileSearchOpen(false); }}
                  className="max-h-[50vh]"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {mobileViewMode === 'map' && (
        <button
          onClick={() => setMobileViewMode('list')}
          className="fixed left-1/2 -translate-x-1/2 z-[999] bg-accent hover:bg-accent/90 text-white px-5 py-2.5 rounded-full shadow-lg flex items-center gap-2 font-medium transition-all active:scale-95"
          style={{ bottom: '72px' }}
          data-testid="mobile-list-toggle"
        >
          <List className="w-5 h-5" />
          <span>List</span>
        </button>
      )}
      
      {mobileViewMode === 'list' && (
        <div className="fixed inset-0 bg-gray-50 z-[998] flex flex-col" style={{ paddingBottom: '56px' }}>
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">{viewportDestinations.length} destinations</h2>
                <p className="text-xs text-gray-500">in current map view</p>
              </div>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-32 h-9 text-sm">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {viewportDestinations.length > 0 ? (
              <div className="space-y-3">
                {viewportDestinations.map((dest) => (
                  <button
                    key={dest.id}
                    onClick={() => onDestinationNavigate(dest)}
                    className="w-full bg-white rounded-xl shadow-sm overflow-hidden text-left active:bg-gray-50 transition-colors"
                    data-testid={`destination-card-${dest.id}`}
                  >
                    <div className="relative w-full h-40">
                      <img 
                        src={dest.image_url || 'https://images.unsplash.com/photo-1500534623283-e43b72fb80f6?w=400'}
                        alt={`${dest.name} - fly fishing destination in ${dest.state}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <button
                        onClick={toggleFavorite}
                        className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
                        data-testid={`mobile-destination-save-${dest.id}`}
                      >
                        <Heart className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-gray-900 text-base">{dest.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {dest.state}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-sm">
                        {dest.target_species?.length > 0 && (
                          <span className="text-gray-600">
                            <Fish className="w-3.5 h-3.5 inline mr-1" />
                            {dest.target_species.slice(0, 2).join(', ')}
                          </span>
                        )}
                        {dest.best_seasons?.length > 0 && (
                          <span className="text-gray-500 text-xs">
                            {dest.best_seasons.slice(0, 2).join(' · ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <MapPin className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-base font-medium">No destinations in view</p>
                <p className="text-sm mt-1">Switch to map and pan to find fishing spots</p>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setMobileViewMode('map')}
            className="fixed left-1/2 -translate-x-1/2 z-[1001] bg-accent hover:bg-accent/90 text-white px-5 py-2.5 rounded-full shadow-lg flex items-center gap-2 font-medium transition-all active:scale-95"
            style={{ bottom: '72px' }}
            data-testid="mobile-map-toggle"
          >
            <Map className="w-5 h-5" />
            <span>Map</span>
          </button>
        </div>
      )}
      
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[1000]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around py-2 px-4">
          <button onClick={() => navigate('/destinations')} className="flex flex-col items-center gap-1 px-3 py-1" data-testid="mobile-tab-explore">
            <Compass className="w-6 h-6 text-gray-900" />
            <span className="text-xs font-medium text-gray-900">Explore</span>
          </button>
          <button onClick={() => navigate('/ai-guide')} className="flex flex-col items-center gap-1 px-3 py-1" data-testid="mobile-tab-ai-guide">
            <Sparkles className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-500">Trip Planner</span>
          </button>
          <button onClick={() => navigate('/app')} className="flex flex-col items-center gap-1 px-3 py-1" data-testid="mobile-tab-get-app">
            <Smartphone className="w-6 h-6 text-accent" />
            <span className="text-xs font-medium text-accent">Get the app</span>
          </button>
        </div>
      </nav>

      <DirectionsDialog
        open={directionsDialogOpen}
        onOpenChange={setDirectionsDialogOpen}
        destination={directionsDestination}
        origin={directionsOrigin}
        onOriginChange={setDirectionsOrigin}
        onGetDirections={getDirections}
        compact
      />
    </div>
  );
}
